/**
 * Loop Details Modal
 * Shows detailed session information for a specific loop pattern
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, ArrowLeftRight, Calendar, Monitor, ExternalLink } from 'lucide-react';
import { api } from '../../services/api';

interface LoopPattern {
  fromPage: string;
  toPage: string;
  sessions: number;
  percentage: number;
}

interface LoopDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  loop: LoopPattern | null;
  companyId: string | null;
  dataSourceId: string | null;
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

interface SessionDetail {
  sessionId: string;
  timestamp: string;
  device: string;
  loopCount: number;
  totalPages: number;
  pages: Array<{
    url: string;
    title: string;
    timestamp: string;
  }>;
  utmSource?: string;
  utmCampaign?: string;
}

export const LoopDetailsModal = ({
  isOpen,
  onClose,
  loop,
  companyId,
  dataSourceId,
  dateRange,
}: LoopDetailsModalProps) => {
  const [expandedSession, setExpandedSession] = useState<string | null>(null);

  // Fetch sessions that exhibit this loop pattern
  const { data: sessionsData, isLoading } = useQuery({
    queryKey: ['loop-sessions', loop?.fromPage, loop?.toPage, companyId, dataSourceId, dateRange],
    queryFn: async () => {
      if (!loop || !companyId || !dataSourceId) return null;

      const response = await api.get('/api/v1/analytics/funnel/loop-sessions', {
        params: {
          companyId,
          dataSourceId,
          fromPage: loop.fromPage,
          toPage: loop.toPage,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
        },
      });

      return response.data.data;
    },
    enabled: isOpen && !!loop && !!companyId && !!dataSourceId,
  });

  if (!isOpen || !loop) return null;

  const sessions: SessionDetail[] = sessionsData?.sessions || [];

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Modal - Responsive: Full screen on mobile, centered on desktop */}
      <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-4">
        <div
          className="bg-white dark:bg-secondary-900 rounded-t-2xl md:rounded-2xl shadow-2xl w-full md:max-w-4xl max-h-[90vh] md:max-h-[85vh] overflow-hidden flex flex-col animate-slide-up"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-start justify-between p-4 md:p-6 border-b border-secondary-200 dark:border-secondary-700 flex-shrink-0">
            <div className="flex-1 min-w-0 mr-4">
              <div className="flex items-center gap-2 mb-2">
                <ArrowLeftRight className="w-5 h-5 text-cyan-500" />
                <h2 className="text-xl font-bold text-secondary-900 dark:text-secondary-50">
                  Loop Pattern Details
                </h2>
              </div>
              
              {/* Loop Path */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                <span className="text-sm font-medium text-secondary-700 dark:text-secondary-300 truncate">
                  {shortenUrl(loop.fromPage)}
                </span>
                <ArrowLeftRight className="w-4 h-4 text-cyan-500 hidden sm:block" />
                <span className="text-sm font-medium text-secondary-700 dark:text-secondary-300 truncate">
                  {shortenUrl(loop.toPage)}
                </span>
              </div>

              {/* Stats */}
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <div className="flex items-center gap-1">
                  <span className="text-secondary-600 dark:text-secondary-400">Sessions:</span>
                  <span className="font-semibold text-cyan-600 dark:text-cyan-400">
                    {loop.sessions.toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-secondary-600 dark:text-secondary-400">Percentage:</span>
                  <span className="font-semibold text-cyan-600 dark:text-cyan-400">
                    {loop.percentage.toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>

            {/* Close button */}
            <button
              onClick={onClose}
              className="p-2 hover:bg-secondary-100 dark:hover:bg-secondary-800 rounded-lg transition-colors flex-shrink-0"
            >
              <X className="w-5 h-5 text-secondary-600 dark:text-secondary-400" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 md:p-6">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="spinner mx-auto mb-4"></div>
                  <p className="text-sm text-secondary-600 dark:text-secondary-400">
                    Loading loop sessions...
                  </p>
                </div>
              </div>
            ) : sessions.length === 0 ? (
              <div className="text-center py-12">
                <ArrowLeftRight className="w-16 h-16 mx-auto mb-4 text-secondary-300 dark:text-secondary-600" />
                <p className="text-secondary-600 dark:text-secondary-400">
                  No session details available for this loop pattern
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-4">
                  Showing {sessions.length} session{sessions.length !== 1 ? 's' : ''} that exhibited this back-and-forth navigation pattern
                </p>

                {sessions.map((session) => (
                  <div
                    key={session.sessionId}
                    className="border border-secondary-200 dark:border-secondary-700 rounded-lg overflow-hidden hover:border-cyan-500 dark:hover:border-cyan-500 transition-colors"
                  >
                    {/* Session Header - Clickable */}
                    <button
                      onClick={() => setExpandedSession(
                        expandedSession === session.sessionId ? null : session.sessionId
                      )}
                      className="w-full p-4 bg-secondary-50 dark:bg-secondary-800/50 hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors text-left"
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-mono text-secondary-500 dark:text-secondary-400 truncate">
                              {session.sessionId.substring(0, 16)}...
                            </span>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-cyan-100 dark:bg-cyan-900/30 text-cyan-800 dark:text-cyan-300">
                              {session.loopCount}× loops
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-3 text-xs text-secondary-600 dark:text-secondary-400">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {new Date(session.timestamp).toLocaleString()}
                            </div>
                            <div className="flex items-center gap-1">
                              <Monitor className="w-3 h-3" />
                              {session.device}
                            </div>
                            <span>{session.totalPages} pages</span>
                            {session.utmSource && (
                              <span className="text-cyan-600 dark:text-cyan-400">
                                📍 {session.utmSource}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex-shrink-0">
                          <ExternalLink className="w-4 h-4 text-secondary-400" />
                        </div>
                      </div>
                    </button>

                    {/* Session Details - Expandable */}
                    {expandedSession === session.sessionId && (
                      <div className="p-4 border-t border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-900">
                        <h4 className="text-sm font-semibold text-secondary-900 dark:text-secondary-100 mb-3">
                          Page Journey ({session.pages.length} pages)
                        </h4>
                        <div className="space-y-2">
                          {session.pages.map((page, idx) => (
                            <div
                              key={idx}
                              className={`flex items-start gap-3 p-2 rounded ${
                                (page.url === loop.fromPage || page.url === loop.toPage)
                                  ? 'bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800'
                                  : 'bg-secondary-50 dark:bg-secondary-800/30'
                              }`}
                            >
                              <span className="text-xs font-mono text-secondary-500 dark:text-secondary-400 flex-shrink-0 mt-0.5">
                                {idx + 1}
                              </span>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-secondary-900 dark:text-secondary-100 truncate">
                                  {page.title || 'Untitled Page'}
                                </div>
                                <div className="text-xs text-secondary-600 dark:text-secondary-400 truncate">
                                  {page.url}
                                </div>
                                <div className="text-xs text-secondary-500 dark:text-secondary-500 mt-1">
                                  {new Date(page.timestamp).toLocaleTimeString()}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

// Helper function to shorten URLs for display
function shortenUrl(url: string): string {
  try {
    // Remove protocol and www
    let short = url.replace(/^https?:\/\/(www\.)?/, '');
    
    // Extract path without query params for display
    const pathMatch = short.match(/^[^?]+/);
    if (pathMatch) {
      short = pathMatch[0];
    }
    
    // Shorten long paths
    if (short.length > 50) {
      const parts = short.split('/');
      if (parts.length > 2) {
        return `${parts[0]}/.../${parts[parts.length - 1]}`;
      }
      return short.substring(0, 47) + '...';
    }
    
    return short;
  } catch (e) {
    return url.substring(0, 50) + (url.length > 50 ? '...' : '');
  }
}

